import React, { useCallback, useEffect, useRef } from "react";
import { cn } from "../../utils/cn";

/**
 * PanelResizer — divisor vertical arrastrable y operable por teclado.
 *
 * Patrón WAI-ARIA "window splitter": role="separator" + tabIndex=0 +
 * aria-valuenow/min/max. El mouse es solo UNA de las formas de usarlo; con el
 * teclado se llega exactamente al mismo resultado (flechas = paso, Home/End =
 * extremos, Enter = ancho por defecto).
 *
 * Rendimiento: el arrastre NO pasa por estado de React. `onChange` se llama a lo
 * sumo una vez por frame (requestAnimationFrame) y el padre escribe el ancho
 * vivo en una CSS var del contenedor; solo `onCommit` (pointerup / teclado)
 * toca el estado persistido. Sin esto, cada pointermove re-renderizaría el
 * inbox entero (lista + detalle) y escribiría en localStorage a 60fps.
 *
 * Props:
 *   value          ancho actual del panel controlado, en px.
 *   min / max      límites duros del panel (max lo calcula el padre según el
 *                  ancho disponible, para no aplastar la conversación).
 *   defaultValue   ancho al que vuelve con doble-click / Enter.
 *   step           paso del teclado en px.
 *   edge           'left'  → divisor pegado al borde izquierdo del contenedor
 *                            (frontera lista↔conversación): arrastrar a la
 *                            derecha AGRANDA el panel.
 *                  'right' → divisor pegado al borde derecho
 *                            (frontera conversación↔contacto): arrastrar a la
 *                            izquierda agranda el panel (signo invertido).
 *   onChange(w)    ancho vivo durante el arrastre (throttled con rAF).
 *   onCommit(w)    ancho final (pointerup, teclado, doble-click).
 *   onResizingChange(bool)  avisa al padre para desactivar la transición del
 *                  grid mientras se arrastra (si no, hace rubber-banding).
 *   label          texto accesible (ya traducido por el padre).
 *   controlsId     id del panel que redimensiona (aria-controls).
 */
export default function PanelResizer({
  value,
  min,
  max,
  defaultValue,
  step = 16,
  edge = "left",
  onChange,
  onCommit,
  onResizingChange,
  label,
  controlsId,
  className,
}) {
  // Signo del arrastre: en el borde derecho, mover el puntero a la izquierda
  // agranda el panel de contacto.
  const direction = edge === "right" ? -1 : 1;

  const dragRef = useRef(null); // { startX, startValue, pointerId, latestX, width }
  const rafRef = useRef(0);
  const nodeRef = useRef(null);

  const clamp = useCallback(
    (next) => Math.min(Math.max(Math.round(next), min), max),
    [min, max]
  );

  // Cursor global + bloqueo de selección: sin esto, arrastrar sobre las burbujas
  // del chat selecciona texto y el cursor parpadea entre col-resize y text.
  const setDragChrome = useCallback((active) => {
    if (typeof document === "undefined") return;
    document.body.style.cursor = active ? "col-resize" : "";
    document.body.style.userSelect = active ? "none" : "";
  }, []);

  const endDrag = useCallback(() => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    setDragChrome(false);
    onResizingChange?.(false);
    try {
      nodeRef.current?.releasePointerCapture?.(drag.pointerId);
    } catch {
      /* el puntero pudo soltarse fuera de la ventana: nada que liberar */
    }
    onCommit?.(drag.width);
  }, [onCommit, onResizingChange, setDragChrome]);

  // Limpieza defensiva: si el divisor se desmonta a mitad de arrastre (cambio de
  // breakpoint, entrar en modo foco) el body no puede quedarse con el cursor
  // col-resize ni con la selección bloqueada.
  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (dragRef.current) {
        dragRef.current = null;
        setDragChrome(false);
        onResizingChange?.(false);
      }
    },
    [onResizingChange, setDragChrome]
  );

  const handlePointerDown = useCallback(
    (event) => {
      // Solo botón principal; los secundarios abren menús contextuales.
      if (event.button !== 0) return;
      event.preventDefault();
      nodeRef.current?.focus?.();
      dragRef.current = {
        startX: event.clientX,
        startValue: value,
        pointerId: event.pointerId,
        latestX: event.clientX,
        width: value,
      };
      try {
        nodeRef.current?.setPointerCapture?.(event.pointerId);
      } catch {
        /* navegador sin captura: el drag sigue funcionando vía este mismo nodo */
      }
      onResizingChange?.(true);
      setDragChrome(true);
    },
    [value, onResizingChange, setDragChrome]
  );

  const handlePointerMove = useCallback(
    (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      drag.latestX = event.clientX;
      if (rafRef.current) return; // ya hay un frame agendado: se usa el último X
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        const current = dragRef.current;
        if (!current) return;
        const next = clamp(
          current.startValue + (current.latestX - current.startX) * direction
        );
        if (next === current.width) return;
        current.width = next;
        onChange?.(next);
      });
    },
    [clamp, direction, onChange]
  );

  const handleKeyDown = useCallback(
    (event) => {
      let next = null;
      switch (event.key) {
        case "ArrowLeft":
          next = value - step * direction;
          break;
        case "ArrowRight":
          next = value + step * direction;
          break;
        case "Home":
          // Home/End son los extremos del VALOR (mínimo/máximo del panel), como
          // en cualquier separator: no dependen de la orientación del borde.
          next = min;
          break;
        case "End":
          next = max;
          break;
        case "Enter":
          next = defaultValue;
          break;
        default:
          return;
      }
      event.preventDefault();
      event.stopPropagation(); // no dispara los atajos de una tecla de la vista
      // El teclado es de baja frecuencia: commitea directo, sin estado vivo.
      onCommit?.(clamp(next));
    },
    [value, step, direction, min, max, defaultValue, onCommit, clamp]
  );

  const handleDoubleClick = useCallback(
    (event) => {
      event.preventDefault();
      onCommit?.(clamp(defaultValue));
    },
    [onCommit, clamp, defaultValue]
  );

  return (
    <div
      ref={nodeRef}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-controls={controlsId}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={Math.round(value)}
      title={label}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={endDrag}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        // Zona de agarre de 10px pegada al borde del contenedor. No sobresale:
        // el <main> tiene overflow-hidden y cualquier saliente se recortaría.
        "group absolute top-0 z-20 h-full w-2.5 cursor-col-resize touch-none",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset",
        edge === "right" ? "right-0" : "left-0",
        className
      )}
    >
      {/* Línea de 1px sobre el borde del panel: invisible en reposo (el borde lo
          sigue dando border-border), naranjo al apuntar, arrastrar o enfocar. */}
      <span
        aria-hidden="true"
        className={cn(
          "absolute inset-y-0 w-px bg-transparent",
          "transition-colors duration-150 ease-out motion-reduce:transition-none",
          "group-hover:bg-brand-500 group-active:bg-brand-500 group-focus-visible:bg-brand-500",
          edge === "right" ? "right-0" : "left-0"
        )}
      />
    </div>
  );
}
